import { FC, ReactNode } from 'react';
import { useFlag } from '@reflag/react-sdk';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Column, Row } from 'further-ui/components/Layout';
import { type Column as TableColumn } from 'further-ui/components/Table';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import { useFilters } from 'further-ui/hooks';
import { AccountingReportDateOption } from 'further-types/lp/general-ledger';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useLayoutContext } from 'contexts/LayoutContext';
import { SyncScrollProvider } from 'contexts/SyncScrollContext';
import PageLoader from 'components/PageLoader';
import PageFilters from 'components/new/PageFilters';
import downloadXlxs from 'utils/downloadXlxs';
import api from 'lib/trpcClient';
import AccountingTable from '../components/AccountingTable';
import AccountingSection from '../components/AccountingSection';
import DatePresetFilter, {
  accountingDateLabels,
} from '../components/DatePresetFilter';
import Alert from '@mui/material/Alert';
import { nonNullable } from 'further-types/utils';

type BalanceSheetRow = {
  id: string;
  label: ReactNode;
  periodEnding: number;
  priorYearEnd: number;
};

const pageId = 'balance-sheet';

const SECONDARY_ROWS = {
  InvestmentsAtCost: 'Investments at cost',
  UnrealisedGainLoss: 'Unrealised gain / (loss) on investments',
};

const TotalText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'bold' && prop !== 'indentation',
})<{ bold?: boolean; indentation?: number }>(
  ({ bold = false, indentation = 0 }) => ({
    fontWeight: bold ? 700 : 500,
    paddingLeft: indentation,
  }),
);

const DataRowLabel = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(theme.spacingSizes.sm),
}));

const SecondaryText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isLabel',
})<{ isLabel?: boolean }>(({ theme, isLabel = false }) => ({
  color: theme.palette.grey[700],
  ...(isLabel && {
    paddingLeft: theme.spacing(theme.spacingSizes.xl),
  }),
}));

const BalanceSheet: FC = () => {
  const { isEnabled: showOtherAccounting } = useFlag('other-accounting');
  const { selectedVehicleId, vehicle } = useLayoutContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { filters, handleChangeFilters } = useFilters<{
    dateOption: AccountingReportDateOption;
    customDate?: CalendarDay;
  }>(pageId, {
    defaultFilters: {
      dateOption: AccountingReportDateOption.EndLastQuarter,
      customDate: undefined,
    },
  });
  const { dateOption, customDate } = filters;
  const isCustomDate = dateOption === AccountingReportDateOption.Custom;
  const isCustomDateUnset = isCustomDate && !customDate;

  const {
    data: balanceSheet,
    isLoading,
    isFetching,
  } = api.generalLedger.balanceSheet.useQuery(
    {
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDate: isCustomDate && customDate ? customDate : undefined,
    },
    {
      enabled: showOtherAccounting && !!selectedVehicleId && !isCustomDateUnset,
    },
  );
  const current = balanceSheet?.current;
  const prior = balanceSheet?.prior;

  const exportBalanceSheet = api.generalLedger.exportBalanceSheet.useMutation(
    useHandleTrpcMutation<{ file: string; fileName: string }>()({
      refetchType: 'none',
      onSuccess: ({ file, fileName }) => {
        downloadXlxs(file, fileName);
      },
    }),
  );

  if (!showOtherAccounting) {
    return null;
  }

  const handleExport = () => {
    if (isCustomDateUnset) return;
    exportBalanceSheet.mutate({
      vehicleId: selectedVehicleId ?? '',
      dateOption,
      customDate: isCustomDate && customDate ? customDate : undefined,
    });
  };

  if (isLoading || !vehicle) {
    return <PageLoader />;
  }

  const { baseCurrency } = vehicle;

  const formatValue = (value: number) =>
    numberToCurrencyString(value, {
      currency: baseCurrency,
      negativeInBrackets: true,
    });

  const renderSecondaryRow = (
    label: ReactNode,
    value: ReactNode,
    isLabel?: boolean,
  ) => {
    const isSecondaryRow =
      typeof label === 'string' &&
      Object.values(SECONDARY_ROWS).includes(label);

    return isSecondaryRow ? (
      <SecondaryText isLabel={isLabel}>{value}</SecondaryText>
    ) : (
      value
    );
  };

  const columns: Array<TableColumn<BalanceSheetRow>> = [
    {
      key: 'label',
      label: 'Accounts',
      width: 275,
      textOverflowEllipsis: true,
      withVerticalBorder: true,
      render: ({ label }) => renderSecondaryRow(label, label, true),
    },
    {
      key: 'periodEnding',
      label: `Period ending (${
        balanceSheet?.currentPeriodEnd?.toReadable() ?? '--'
      })`,
      width: 125,
      noWrap: true,
      withVerticalBorder: true,
      render: ({ label, periodEnding }) =>
        renderSecondaryRow(label, formatValue(periodEnding)),
    },
    {
      key: 'priorYearEnd',
      label: `Prior year end (${
        balanceSheet?.priorYearEnd?.toReadable() ?? '--'
      })`,
      width: 125,
      noWrap: true,
      render: ({ label, priorYearEnd }) =>
        renderSecondaryRow(label, formatValue(priorYearEnd)),
    },
  ];

  const renderRow = (
    id: string,
    label: string,
    periodEnding = 0,
    priorYearEnd = 0,
  ): BalanceSheetRow | undefined =>
    periodEnding !== 0 || priorYearEnd !== 0
      ? {
          id,
          label: Object.values(SECONDARY_ROWS).includes(label) ? (
            label
          ) : (
            <DataRowLabel variant="h6" fontSize="inherit" lineHeight="inherit">
              {label}
            </DataRowLabel>
          ),
          periodEnding,
          priorYearEnd,
        }
      : undefined;

  const investmentRows = [
    renderRow(
      'investmentsAtFairValue',
      'Investments at fair value',
      current?.investmentsAtFairValue,
      prior?.investmentsAtFairValue,
    ),
    renderRow(
      'investmentsAtCost',
      SECONDARY_ROWS.InvestmentsAtCost,
      current?.investmentsAtCost,
      prior?.investmentsAtCost,
    ),
    renderRow(
      'unrealisedGainLoss',
      SECONDARY_ROWS.UnrealisedGainLoss,
      current?.unrealisedGainLoss,
      prior?.unrealisedGainLoss,
    ),
  ].filter(nonNullable);

  const assetRows = [
    renderRow(
      'cashAndCashEquivalents',
      'Cash and cash equivalents',
      current?.cashAndCashEquivalents,
      prior?.cashAndCashEquivalents,
    ),
    renderRow(
      'receivablesAndPrepayments',
      'Receivables and prepayments',
      current?.receivablesAndPrepayments,
      prior?.receivablesAndPrepayments,
    ),
    renderRow(
      'accruedIncome',
      'Accrued income',
      current?.accruedIncome,
      prior?.accruedIncome,
    ),
    renderRow(
      'capitalDueFromPartners',
      'Capital due from partners',
      current?.capitalDueFromPartners,
      prior?.capitalDueFromPartners,
    ),
  ].filter(nonNullable);
  const hasAssets =
    investmentRows.length > 0 ||
    assetRows.length > 0 ||
    (current?.totalAssets ?? 0) !== 0 ||
    (prior?.totalAssets ?? 0) !== 0;

  const liabilityRows = [
    renderRow(
      'accountsPayable',
      'Accounts payable',
      current?.accountsPayable,
      prior?.accountsPayable,
    ),
    renderRow(
      'accruedExpenses',
      'Accrued expenses',
      current?.accruedExpenses,
      prior?.accruedExpenses,
    ),
    renderRow(
      'capitalDueToPartners',
      'Capital due to partners',
      current?.capitalDueToPartners,
      prior?.capitalDueToPartners,
    ),
  ].filter(nonNullable);
  const hasLiabilities =
    liabilityRows.length > 0 ||
    (current?.totalLiabilities ?? 0) !== 0 ||
    (prior?.totalLiabilities ?? 0) !== 0;

  const partnerRows = [
    renderRow(
      'generalPartnerCapital',
      'General partner',
      current?.generalPartnerCapital,
      prior?.generalPartnerCapital,
    ),
    renderRow(
      'carryPartnerCapital',
      'Carry partner',
      current?.carryPartnerCapital,
      prior?.carryPartnerCapital,
    ),
    renderRow(
      'limitedPartnersCapital',
      'Limited partners',
      current?.limitedPartnersCapital,
      prior?.limitedPartnersCapital,
    ),
  ].filter(nonNullable);
  const hasPartners =
    partnerRows.length > 0 ||
    (current?.totalPartnersCapital ?? 0) !== 0 ||
    (prior?.totalPartnersCapital ?? 0) !== 0;
  const hasData = hasAssets || hasLiabilities || hasPartners;
  const noData = !hasData;

  return (
    <Column spacing="lg" fullHeight>
      <Row centered spacing="sm" justify="space-between" breakpoint="md">
        <Column spacing="xs">
          <Typography variant="h5" color="grey.900">
            Balance sheet
          </Typography>
          <Typography variant="body2" noWrap={!isMobile}>
            {vehicle.vehicleName} as at{' '}
            {isCustomDate
              ? isCustomDateUnset
                ? '...'
                : customDate?.toReadable()
              : accountingDateLabels[dateOption].toLowerCase()}
            {isCustomDate ? (isCustomDateUnset ? '' : '.') : '.'}
          </Typography>
        </Column>
        <PageFilters
          justify="flex-end"
          align="center"
          actions={[
            {
              label: 'Export',
              variant: 'contained',
              onClick: handleExport,
              disabled: isCustomDateUnset,
            },
          ]}
        >
          <DatePresetFilter
            dateOption={dateOption}
            customDate={customDate}
            onChange={(dateOption, customDate) =>
              handleChangeFilters({ dateOption, customDate })
            }
          />
        </PageFilters>
      </Row>
      {balanceSheet && !balanceSheet.isBalanced && !isFetching && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Balance sheet does not balance. Net assets (
          {numberToCurrencyString(balanceSheet.current.netAssets, {
            currency: vehicle?.baseCurrency,
          })}
          ) do not equal partners' capital (
          {numberToCurrencyString(balanceSheet.current.totalPartnersCapital, {
            currency: vehicle?.baseCurrency,
          })}
          ). This indicates a general ledger data integrity issue that needs
          investigation.
        </Alert>
      )}
      <SyncScrollProvider>
        <Column fullHeight>
          <AccountingTable
            columns={columns}
            rows={[]}
            isLoading={isFetching}
            borderless={hasData}
            bodyRowPaddingY={noData ? 30 : undefined}
            emptyMessage={noData ? 'No data.' : undefined}
          />
          {isFetching ? null : (
            <>
              {hasAssets ? (
                <AccountingSection
                  title="Assets"
                  titleVariant="h6"
                  expandable={false}
                  noTopBorder
                >
                  <AccountingTable
                    columns={columns}
                    rows={investmentRows}
                    bottomBorderOnly
                    hideHeader
                    customRowVerticalBorder
                  />
                  <AccountingTable
                    columns={columns}
                    rows={assetRows}
                    borderless
                    hideHeader
                    customRowVerticalBorder
                    customFooterRow={[
                      {
                        key: 'total-assets',
                        content: (
                          <TotalText variant="h6" indentation={8}>
                            Total assets
                          </TotalText>
                        ),
                        backgroundColor: grey[50],
                      },
                      {
                        key: 'periodEnding',
                        content: (
                          <TotalText variant="h6">
                            {formatValue(current?.totalAssets ?? 0)}
                          </TotalText>
                        ),
                        backgroundColor: grey[50],
                      },
                      {
                        key: 'priorYearEnd',
                        content: (
                          <TotalText variant="h6">
                            {formatValue(prior?.totalAssets ?? 0)}
                          </TotalText>
                        ),
                        backgroundColor: grey[50],
                      },
                    ]}
                  />
                </AccountingSection>
              ) : null}
              {hasLiabilities ? (
                <AccountingSection
                  title="Liabilities"
                  titleVariant="h6"
                  expandable={false}
                  noTopBorder
                >
                  <AccountingTable
                    columns={columns}
                    rows={liabilityRows}
                    borderless
                    hideHeader
                    customRowVerticalBorder
                    customFooterRow={[
                      {
                        key: 'total-liabilities',
                        content: (
                          <TotalText variant="h6" indentation={8}>
                            Total liabilities
                          </TotalText>
                        ),
                        backgroundColor: grey[50],
                      },
                      {
                        key: 'periodEnding',
                        content: (
                          <TotalText variant="h6">
                            {formatValue(current?.totalLiabilities ?? 0)}
                          </TotalText>
                        ),
                        backgroundColor: grey[50],
                      },
                      {
                        key: 'priorYearEnd',
                        content: (
                          <TotalText variant="h6">
                            {formatValue(prior?.totalLiabilities ?? 0)}
                          </TotalText>
                        ),
                        backgroundColor: grey[50],
                      },
                    ]}
                  />
                </AccountingSection>
              ) : null}
              {hasAssets || hasLiabilities ? (
                <AccountingTable
                  columns={columns}
                  rows={[]}
                  noTopBorder
                  hideHeader
                  displayFooterIfEmptyTable
                  customRowVerticalBorder
                  customFooterRow={[
                    {
                      key: 'net-assets-liabilities',
                      content: (
                        <TotalText variant="h6" bold>
                          Net assets / (liabilities)
                        </TotalText>
                      ),
                      backgroundColor: grey[100],
                    },
                    {
                      key: 'periodEnding',
                      content: (
                        <TotalText variant="h6" bold>
                          {formatValue(current?.netAssets ?? 0)}
                        </TotalText>
                      ),
                      backgroundColor: grey[100],
                    },
                    {
                      key: 'priorYearEnd',
                      content: (
                        <TotalText variant="h6" bold>
                          {formatValue(prior?.netAssets ?? 0)}
                        </TotalText>
                      ),
                      backgroundColor: grey[100],
                    },
                  ]}
                />
              ) : null}
              {hasPartners ? (
                <AccountingSection
                  title="Partners' capital"
                  titleVariant="h6"
                  expandable={false}
                  noTopBorder
                  customRow={
                    <AccountingTable
                      columns={columns}
                      rows={[]}
                      topBorderOnly
                      hideHeader
                      displayFooterIfEmptyTable
                      customRowVerticalBorder
                      customFooterRow={[
                        {
                          key: 'total-partners',
                          content: (
                            <TotalText variant="h6" bold>
                              Total partners' capital
                            </TotalText>
                          ),
                          backgroundColor: grey[100],
                        },
                        {
                          key: 'periodEnding',
                          content: (
                            <TotalText variant="h6" bold>
                              {formatValue(current?.totalPartnersCapital ?? 0)}
                            </TotalText>
                          ),
                          backgroundColor: grey[100],
                        },
                        {
                          key: 'priorYearEnd',
                          content: (
                            <TotalText variant="h6" bold>
                              {formatValue(prior?.totalPartnersCapital ?? 0)}
                            </TotalText>
                          ),
                          backgroundColor: grey[100],
                        },
                      ]}
                    />
                  }
                >
                  <AccountingTable
                    columns={columns}
                    rows={partnerRows}
                    borderless
                    hideHeader
                  />
                </AccountingSection>
              ) : null}
            </>
          )}
        </Column>
      </SyncScrollProvider>
    </Column>
  );
};

export default BalanceSheet;
